import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import sortBy from '@nullvoxpopuli/ember-composable-helpers/helpers/sort-by';
import { task, type Task } from 'ember-concurrency';
import { or } from 'ember-truth-helpers';
import type Flow from 'garaje/models/flow';
import type SignInField from 'garaje/models/sign-in-field';
import { filter } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

interface SignInFieldsRespondentsSignature {
  Args: {
    signInFields: SignInField[];
    flow: Flow;
    saveFieldsTask: Task<void, []>;
    isDisabled?: boolean;
    isPristine: boolean;
    onChange?: (field: SignInField, attr: string) => void;
  };
}

const HIDDEN_FIELD_IDENTIFIERS = Object.freeze(['host', 'name']);

const configurableFieldsFilterFn = (field: SignInField): boolean => {
  return !HIDDEN_FIELD_IDENTIFIERS.includes(field.identifier);
};

/**
 * Component for managing who can respond to each field during invitation and sign-in processes.
 */
export default class SignInFieldsRespondents extends Component<SignInFieldsRespondentsSignature> {
  @localCopy('args.signInFields') declare signInFields: SignInField[];

  optionsName = 'respondents';

  get isCTAVisible(): boolean {
    if (this.args.isDisabled) return false;

    return this.configurableFields.length > 0;
  }

  get isCTADisabled(): boolean {
    return this.args.isDisabled || this.args.isPristine || this.saveRespondentsOptionsTask.isRunning;
  }

  get isNotEmpty(): boolean {
    return this.configurableFields.length > 0;
  }

  @filter('signInFields', configurableFieldsFilterFn)
  declare configurableFields: SignInField[];

  @action
  onChange(field: SignInField, attr: string): void {
    this.args.onChange?.(field, attr);
  }

  @action
  updateAllowVisitorRespondents(field: SignInField, event: Event): void {
    field.allowVisitorRespondents = (event.target as HTMLInputElement).checked;
    this.onChange(field, 'allowVisitorRespondents');
  }

  @action
  updateAllowEmployeeRespondents(field: SignInField, event: Event): void {
    field.allowEmployeeRespondents = (event.target as HTMLInputElement).checked;
    this.onChange(field, 'allowEmployeeRespondents');
  }

  saveRespondentsOptionsTask = task(async (): Promise<void> => {
    await this.args.saveFieldsTask.perform();
  });

  <template>
    <div class="px-8 overflow-none" data-test-field-options={{this.optionsName}}>
      <p class="text-sm text-carbon-60 mb-6 pr-12">
        Determine who can respond to each field during the invitation and sign-in processes. Learn more about
        <a
          href="https://envoy.help/en/articles/5635538-setting-up-hidden-sign-in-fields"
          target="_blank"
          rel="noopener"
        >
          respondents →
        </a>
      </p>

      {{#if this.isNotEmpty}}
        <div class="mb-8">
          <div data-test-field-list class="mt-6">
            <div class="flex text-carbon-50 font-bold">
              <span class="flex text-sm" style="flex:2">Field</span>
              <span id="respondentsHeadingVisitors" class="flex-1 flex text-sm justify-center">Visitors</span>
              <span id="respondentsHeadingEmployees" class="flex-1 flex text-sm justify-center">Employees</span>
              <span id="respondentsHeadingAdmin" class="flex-1 flex text-sm justify-center">Admin</span>
            </div>

            <hr class="my-2" />

            <div class="overflow-y-auto overscroll-y-contain" style="max-height:40vh;">
              {{#each (sortBy "position" this.configurableFields) as |field|}}
                <div data-test-configurable-field={{field.id}} class="flex">
                  <p class="flex text-sm text-carbon-50 m-0" style="flex:2">
                    {{field.name}}
                  </p>

                  <div class="flex-1 flex items-center text-center">
                    <input
                      data-test-allow-visitors
                      name="allow-visitors-{{field.id}}"
                      disabled={{or @isDisabled field.isAllowVisitorRespondentsLocked}}
                      aria-label="Allow Visitors to Respond to: {{field.name}}?"
                      class="m-auto"
                      type="checkbox"
                      checked={{field.allowVisitorRespondents}}
                      {{on "change" (fn this.updateAllowVisitorRespondents field)}}
                    />
                  </div>

                  <div class="flex-1 flex items-center">
                    <input
                      data-test-allow-employees
                      name="allow-employees-{{field.id}}"
                      disabled={{or @isDisabled field.isAllowEmployeeRespondentsLocked}}
                      aria-label="Allow Employees to Respond to: {{field.name}}?"
                      class="m-auto"
                      type="checkbox"
                      checked={{field.allowEmployeeRespondents}}
                      {{on "change" (fn this.updateAllowEmployeeRespondents field)}}
                    />
                  </div>

                  <div class="flex-1 flex items-center">
                    <input
                      data-test-allow-admin
                      name="allow-admin-{{field.id}}"
                      disabled="true"
                      aria-label="Allow Admin to Respond to: {{field.name}}?"
                      class="m-auto"
                      type="checkbox"
                      checked="true"
                    />
                  </div>
                </div>

                <hr class="my-2" />
              {{/each}}
            </div>
          </div>
        </div>
      {{else}}
        <p data-test-empty-text class="text-center text-carbon-40 mt-12 mb-24">
          You have no fields to
          {{if @isDisabled "view" "manage"}}.
        </p>
      {{/if}}

      {{#if this.isCTAVisible}}
        <button
          disabled={{this.isCTADisabled}}
          class="btn btn--small btn--brand float-right mb-8
            {{if this.saveRespondentsOptionsTask.isRunning 'disabled'}}
            "
          {{on "click" this.saveRespondentsOptionsTask.perform}}
          data-test-save-options={{this.optionsName}}
        >
          {{#if this.saveRespondentsOptionsTask.isRunning}}
            Saving...
          {{else}}
            Save
          {{/if}}
        </button>
      {{/if}}
    </div>
  </template>
}
